/**
 * InvoiceOcrPage — nhập hoá đơn bằng AI, duyệt, rồi ghi vào MeInvoice.
 *
 * Đặt cạnh MeInvoicePage có chủ ý: kế toán duyệt xong là quản lý tiếp chính
 * hoá đơn vừa tạo, ngay trong app này.
 *
 * Ba điều quyết định hình dạng màn này:
 *
 *  1. KHÔNG ghi gì cho tới khi người duyệt bấm xác nhận. AI đọc ảnh chụp, và
 *     hoá đơn sai là một khoản chi sai cho giáo viên.
 *  2. Đọc chạy ở worker nền nên phải POLL. Upload xong trang chưa có dữ liệu.
 *  3. `blockBatching` mặc định BẬT — hoá đơn ghi xong vẫn bị chặn khỏi lệnh
 *     chi tiền cho tới khi có người chủ động mở. Tắt nó là một hành động có ý
 *     thức, nên nó là một switch riêng chứ không nằm lẫn trong nút xác nhận.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Modal, ProgressBar, Row, Spinner, Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import invoiceOcrService from '../services/invoiceOcr';
import drivingApi from '../services/driving';
import AdminLogoutButton from '../components/AdminLogoutButton';
import DoiChieuMisaModal from '../components/DoiChieuMisaModal';
import TeacherPicker from '../components/TeacherPicker';
import { trangThaiMisa, TRANG_THAI_MISA } from '../utils/trangThaiMisa';
// Luật 'ghi được không' — khớp backend, có test riêng (utils/hoaDonDaChi.test.js).
import { canXacNhanDoiNguoi, gheDuoc } from '../utils/hoaDonDaChi';
// "Trùng với dòng nào" — thuần client, test riêng (utils/invoiceOcrTrung.test.js).
import { timDongTrungKhac, nhanDong } from '../utils/invoiceOcrTrung';
// "Mã giáo viên này là mã đoán?" — test riêng (utils/invoiceOcrGiaoVien.test.js).
import { maDoan } from '../utils/invoiceOcrGiaoVien';

const MATCH = {
  new: { text: 'Mới', bg: 'primary', hint: 'Chưa có trong hệ thống — sẽ tạo hoá đơn mới.' },
  matched: { text: 'Đã có', bg: 'success', hint: 'Đã có trong hệ thống, chưa chi — chỉ gán giáo viên/trung tâm. Đổi người nhận phải xác nhận.' },
  duplicate: { text: 'Trùng', bg: 'secondary', hint: 'Trùng với một dòng khác trong cùng đợt — sẽ bỏ qua.' },
  unidentifiable: { text: 'Không đọc được mã', bg: 'danger', hint: 'Thiếu ký hiệu/số/MST người bán — không có khoá chống trùng, không ghi được.' },
  pending: { text: '—', bg: 'light', hint: '' },
};

const money = (n) => (typeof n === 'number' ? n.toLocaleString('vi-VN') : '—');
const ngay = (v) => (v ? String(v).slice(0, 10) : '—');

/** Giá trị cuối cùng của một trường: sửa tay đè lên AI đọc. */
const valueOf = (item, field) => {
  if (item?.corrections && Object.prototype.hasOwnProperty.call(item.corrections, field)) {
    return item.corrections[field];
  }
  return item?.extracted?.[field];
};
const daSua = (item, field) => !!item?.corrections
  && Object.prototype.hasOwnProperty.call(item.corrections, field);

export default function InvoiceOcrPage() {
  const [batches, setBatches] = useState([]);
  const [current, setCurrent] = useState(null);      // { batch, items }
  const [teachers, setTeachers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const [committing, setCommitting] = useState(false);
  const [choPhepChiTien, setChoPhepChiTien] = useState(false);
  const [bulkTeacher, setBulkTeacher] = useState('');
  const [chon, setChon] = useState(() => new Set());
  // Khoá từng dòng đang gửi: nút bấm được hai lần liên tiếp dễ hơn ô tick
  // nhiều, mà hai PATCH ngược nhau trên cùng một dòng thì kết quả tùy thứ tự về.
  const [dangDuyet, setDangDuyet] = useState(() => new Set());
  const [moDoiChieu, setMoDoiChieu] = useState(false);
  const [guiDoiChieu, setGuiDoiChieu] = useState(false);
  // Kết quả lần Ghi gần nhất: các dòng bị bỏ qua và lý do. Toast biến mất sau
  // vài giây, còn "3 hoá đơn đã chi, không ghi" là thứ kế toán cần đọc kỹ.
  const [ketQuaGhi, setKetQuaGhi] = useState(null);
  // Dòng đang mở ô sửa ký hiệu/số/MST — khi AI đọc nhầm, đây là cách DUY NHẤT
  // thoát khỏi "Trùng" sai (backend tự tính lại trùng-trong-đợt sau mỗi lần
  // sửa định danh — yêu cầu kế toán, 13/10/2026).
  const [dangSua, setDangSua] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try {
      const res = await invoiceOcrService.listBatches({ _limit: 20 });
      setBatches(res?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Không tải được danh sách đợt nhập');
    }
  }, []);

  const loadBatch = useCallback(async (id) => {
    try {
      const res = await invoiceOcrService.getBatch(id);
      setCurrent(res?.data || null);
      return res?.data?.batch?.status;
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Không tải được đợt nhập');
      return null;
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  useEffect(() => {
    let huy = false;
    drivingApi
      .listInvoiceDrivingTeachers(1, 500)
      .then((res) => { if (!huy) setTeachers(res?.data || []); })
      .catch(() => { /* không có giáo viên thì ô chọn để trống, không doạ người dùng */ });
    return () => { huy = true; };
  }, []);

  // Poll trong lúc worker đang đọc hoặc đang đối chiếu MISA. Dừng NGAY khi
  // xong — để chạy mãi thì một tab bỏ quên sẽ gọi API vô hạn.
  const dangDoiChieu = current?.batch?.doiChieu?.trangThai === 'dang-chay';
  useEffect(() => {
    const status = current?.batch?.status;
    const id = current?.batch?._id;
    if (!id || (status !== 'extracting' && !dangDoiChieu)) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(() => { loadBatch(id); }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [current?.batch?.status, current?.batch?._id, dangDoiChieu, loadBatch]);

  const batDauDoiChieu = useCallback(async () => {
    const id = current?.batch?._id;
    if (!id) return;
    setGuiDoiChieu(true);
    try {
      await invoiceOcrService.doiChieuLaiMisa(id);
      await loadBatch(id);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setGuiDoiChieu(false);
    }
  }, [current?.batch?._id, loadBatch]);

  const upload = useCallback(async () => {
    const files = [...(fileRef.current?.files || [])];
    if (!files.length) return toast.error('Chưa chọn file nào');
    setUploading(true);
    setPct(0);
    try {
      const res = await invoiceOcrService.createBatch(files, {
        onUploadProgress: (e) => {
          if (e.total) setPct(Math.round((e.loaded * 100) / e.total));
        },
      });
      const id = res?.data?._id || res?.data?.batch?._id;
      if (fileRef.current) fileRef.current.value = '';
      await loadBatches();
      if (id) await loadBatch(id);
      toast.success('Đã nhận file, AI đang đọc…');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setUploading(false);
      setPct(0);
    }
    return undefined;
  }, [loadBatch, loadBatches]);

  const suaDong = useCallback(async (id, data) => {
    try {
      await invoiceOcrService.updateItem(id, data);
      if (current?.batch?._id) await loadBatch(current.batch._id);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    }
  }, [current?.batch?._id, loadBatch]);

  /** Bật/tắt duyệt một dòng. Đảo trạng thái hiện tại chứ không chỉ đặt true:
   * người duyệt nhần một dòng thì phải rút lại được trước khi bấm ghi. */
  const doiDuyet = useCallback(async (item) => {
    if (dangDuyet.has(item._id)) return;
    setDangDuyet((s) => new Set(s).add(item._id));
    try {
      await suaDong(item._id, { reviewed: !item.reviewed });
    } finally {
      setDangDuyet((s) => {
        const n = new Set(s);
        n.delete(item._id);
        return n;
      });
    }
  }, [dangDuyet, suaDong]);

  const ganHangLoat = useCallback(async () => {
    if (!bulkTeacher) return toast.error('Chưa chọn giáo viên');
    if (!chon.size) return toast.error('Chưa chọn dòng nào');
    // Hoá đơn đã chi không gán lại được — bỏ qua thay vì gửi rồi để backend
    // từ chối lúc Ghi.
    const daChi = new Set((current?.items || []).filter((i) => i.matchedPaid).map((i) => i._id));
    let boQua = 0;
    for (const id of chon) {
      if (daChi.has(id)) { boQua += 1; continue; }
      // Tuần tự có chủ ý: gán hàng loạt vài chục dòng mà bắn song song thì
      // backend nhận một loạt ghi cùng lúc lên cùng một batch.
      // eslint-disable-next-line no-await-in-loop
      await invoiceOcrService.updateItem(id, { drivingTeacher: bulkTeacher, reviewed: true });
    }
    setChon(new Set());
    if (current?.batch?._id) await loadBatch(current.batch._id);
    toast.success('Đã gán giáo viên');
    if (boQua) toast.warning(`Bỏ qua ${boQua} hoá đơn đã chi — không đổi người nhận.`);
    return undefined;
  }, [bulkTeacher, chon, current?.items, current?.batch?._id, loadBatch]);

  // `current?.items || []` viết thẳng sẽ sinh một mảng MỚI mỗi lần render, làm
  // useMemo bên dưới không bao giờ trúng cache — tức là lọc lại toàn bộ danh
  // sách trên mọi lần gõ phím. Neo nó vào đúng `current?.items`.
  const items = useMemo(() => current?.items || [], [current?.items]);
  const sanSang = useMemo(() => items.filter(gheDuoc), [items]);

  const ghi = useCallback(async () => {
    if (!sanSang.length) return toast.error('Không có dòng nào đủ điều kiện ghi');
    const canhBao = choPhepChiTien
      ? '\n\nCẢNH BÁO: đã BẬT cho phép vào lệnh chi tiền. Hoá đơn sẽ đi thẳng vào batch chi cho giáo viên.'
      : '\n\nHoá đơn sẽ bị CHẶN khỏi lệnh chi tiền cho tới khi có người mở.';
    if (!window.confirm(`Ghi ${sanSang.length} hoá đơn vào hệ thống?${canhBao}`)) return undefined;
    setCommitting(true);
    try {
      const res = await invoiceOcrService.commitBatch(current.batch._id, { blockBatching: !choPhepChiTien });
      const kq = res?.data || null;
      setKetQuaGhi(kq);
      const ghiDuoc = (kq?.created || 0) + (kq?.updated || 0);
      toast.success(`Đã ghi ${ghiDuoc} hoá đơn`);
      if (kq?.skipped) toast.warning(`${kq.skipped} dòng bị bỏ qua — xem lý do ở đầu bảng.`);
      await loadBatch(current.batch._id);
      await loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setCommitting(false);
    }
    return undefined;
  }, [sanSang.length, choPhepChiTien, current?.batch?._id, loadBatch, loadBatches]);

  const b = current?.batch;

  return (
    <Container fluid className='p-3'>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <h5 className='mb-0'>Nhập hoá đơn bằng AI</h5>
        <div className='d-flex gap-2'>
          <Button size='sm' variant='outline-secondary' href='/admin/meinvoice'>Hoá đơn</Button>
          <Button size='sm' variant='outline-secondary' href='/admin/meinvoice/doi-chieu'>Đối chiếu bảng kê</Button>
          <AdminLogoutButton size='sm' />
        </div>
      </div>

      <Card className='mb-3'>
        <Card.Body>
          <Row className='g-2 align-items-end'>
            <Col md={7}>
              <Form.Label className='fw-semibold mb-1'>Ảnh hoặc PDF hoá đơn</Form.Label>
              <Form.Control ref={fileRef} type='file' multiple accept='image/*,.pdf' />
            </Col>
            <Col md={5}>
              <Button onClick={upload} disabled={uploading}>
                {uploading ? <><Spinner size='sm' animation='border' className='me-2' />Đang tải lên…</> : 'Tải lên và đọc'}
              </Button>
            </Col>
          </Row>
          {uploading && pct > 0 && <ProgressBar className='mt-2' now={pct} label={`${pct}%`} />}
        </Card.Body>
      </Card>

      <Row className='g-3'>
        <Col md={3}>
          <Card>
            <Card.Header className='py-2 fw-semibold'>Các đợt nhập</Card.Header>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {batches.length === 0 && <div className='p-3 text-muted small'>Chưa có đợt nào</div>}
              {batches.map((x) => (
                <button
                  key={x._id}
                  type='button'
                  className={`btn btn-link text-start w-100 px-3 py-2 border-bottom text-decoration-none ${b?._id === x._id ? 'bg-light' : ''}`}
                  onClick={() => { setKetQuaGhi(null); loadBatch(x._id); }}
                >
                  <div className='small fw-semibold text-dark'>
                    {new Date(x.createdAt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                  </div>
                  <div className='small text-muted'>
                    {x.itemCount || 0} dòng · {x.status}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </Col>

        <Col md={9}>
          {!b && <div className='text-muted'>Chọn một đợt nhập ở bên trái.</div>}

          {b && (
            <Card>
              <Card.Body>
                <div className='d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3'>
                  <div>
                    <div className='fw-bold'>
                      Đợt {new Date(b.createdAt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                      {' '}
                      <Badge bg={b.status === 'committed' ? 'success' : (b.status === 'extracting' ? 'info' : 'secondary')}>
                        {b.status}
                      </Badge>
                    </div>
                    <div className='text-muted small'>
                      {items.length} dòng · {sanSang.length} sẵn sàng ghi
                      {b.status === 'extracting' && <> · <Spinner size='sm' animation='border' className='ms-1' /> AI đang đọc</>}
                    </div>
                  </div>
                  <div className='d-flex gap-2 align-items-center'>
                    <Button
                      variant='outline-primary'
                      disabled={b.status === 'extracting' || b.status === 'pending'}
                      onClick={() => setMoDoiChieu(true)}
                      title='Kéo hoá đơn trong khoảng ngày của đợt này từ MISA về và khớp lại từng dòng'
                    >
                      {dangDoiChieu && <Spinner size='sm' animation='border' className='me-2' />}
                      Đối chiếu lại MISA
                    </Button>
                    <Form.Check
                      type='switch'
                      id='ocr-cho-chi-tien'
                      label='Cho vào lệnh chi tiền'
                      checked={choPhepChiTien}
                      onChange={(e) => setChoPhepChiTien(e.target.checked)}
                      title='Mặc định TẮT: hoá đơn ghi xong vẫn bị chặn khỏi lệnh chi cho tới khi có người chủ động mở.'
                    />
                    <Button
                      variant='success'
                      disabled={committing || b.status === 'committed' || !sanSang.length}
                      onClick={ghi}
                    >
                      {committing ? <><Spinner size='sm' animation='border' className='me-2' />Đang ghi…</> : `Ghi ${sanSang.length} hoá đơn`}
                    </Button>
                  </div>
                </div>

                <InputGroup size='sm' className='mb-2' style={{ maxWidth: 520 }}>
                  <InputGroup.Text>Gán giáo viên cho {chon.size} dòng đã chọn</InputGroup.Text>
                  <div className='flex-grow-1'>
                    <TeacherPicker value={bulkTeacher} onChange={setBulkTeacher} teachers={teachers} />
                  </div>
                  <Button variant='outline-primary' onClick={ganHangLoat} disabled={!chon.size || !bulkTeacher}>Gán</Button>
                </InputGroup>

                {ketQuaGhi?.skipped > 0 && (
                  <Alert variant='warning' dismissible onClose={() => setKetQuaGhi(null)} className='py-2 small'>
                    <div className='fw-semibold mb-1'>
                      Lần ghi vừa rồi: ghi {(ketQuaGhi.created || 0) + (ketQuaGhi.updated || 0)}, bỏ qua {ketQuaGhi.skipped}
                    </div>
                    <ul className='mb-0 ps-3'>
                      {(ketQuaGhi.details || []).filter((d) => d.status === 'skipped').map((d) => (
                        <li key={d.item} className={d.code === 'da_chi' ? 'text-danger' : undefined}>
                          <span className='fw-semibold'>{d.label}</span>: {d.reason}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <div style={{ maxHeight: '62vh', overflow: 'auto' }}>
                  <Table size='sm' hover className='align-middle'>
                    <thead className='table-light position-sticky top-0'>
                      <tr>
                        <th style={{ width: 32 }} />
                        <th>Ký hiệu / Số</th>
                        <th>Ngày</th>
                        <th>MST bán</th>
                        <th className='text-end'>Tổng tiền</th>
                        <th>Biển số</th>
                        <th>Khớp</th>
                        <th>MISA</th>
                        <th>Giáo viên</th>
                        <th className='text-center'>Duyệt</th>
                        <th>Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => {
                        const m = MATCH[it.matchStatus] || MATCH.pending;
                        const misa = TRANG_THAI_MISA[trangThaiMisa(it)];
                        const daChi = !!it.matchedPaid;
                        const canXacNhan = !daChi && canXacNhanDoiNguoi(it);
                        let lopDong;
                        if (daChi) lopDong = 'table-danger';
                        else if (it.mathOk === false) lopDong = 'table-warning';
                        return (
                          <tr key={it._id} className={lopDong}>
                            <td>
                              <Form.Check
                                checked={chon.has(it._id)}
                                onChange={(e) => {
                                  const s = new Set(chon);
                                  if (e.target.checked) s.add(it._id); else s.delete(it._id);
                                  setChon(s);
                                }}
                              />
                            </td>
                            <td className={daSua(it, 'Series') || daSua(it, 'InvoiceNo') ? 'fw-semibold text-primary' : ''}>
                              {valueOf(it, 'Series') || '—'} / {valueOf(it, 'InvoiceNo') || '—'}
                              {it.status !== 'committed' && (
                                <Button size='sm' variant='link' className='p-0 ms-1 small'
                                  title='Sửa ký hiệu/số/MST khi AI đọc sai — việc này quyết định "Trùng" đúng hay sai'
                                  onClick={() => setDangSua(it)}>
                                  Sửa mã
                                </Button>
                              )}
                            </td>
                            <td>{ngay(valueOf(it, 'InvoiceDate'))}</td>
                            <td>{valueOf(it, 'SellerTaxCode') || '—'}</td>
                            <td className='text-end'>{money(valueOf(it, 'TotalAmount'))}</td>
                            <td>{valueOf(it, 'LicensePlate') || '—'}</td>
                            <td>
                              {daChi ? (
                                /* Ca 00831156 (10/2026): hoá đơn đã chi cho người khác
                                   chỉ hiện "Đã có", và Lưu đổi luôn người nhận. Đã chi
                                   thì backend bỏ qua dòng này — nói rõ ngay ở đây. */
                                <Badge bg='danger' title={it.matchedPaid.reason}>ĐÃ CHI</Badge>
                              ) : (
                                <Badge bg={m.bg} text={m.bg === 'light' ? 'dark' : undefined} title={m.hint}>{m.text}</Badge>
                              )}
                              {daChi && (
                                <div className='small text-danger' style={{ maxWidth: 220 }}>{it.matchedPaid.reason}</div>
                              )}
                              {!daChi && it.matchStatus === 'matched' && it.matchedTeacher && (
                                <div className='small text-muted'>Đang gán: {it.matchedTeacher}</div>
                              )}
                              {/* Trước đây chỉ hiện chữ "Trùng", không nói trùng với
                                  dòng nào — người duyệt không có cách kiểm tra đúng/sai
                                  (yêu cầu kế toán, 13/10/2026). Tên file ở đây để mở
                                  hai ảnh so sánh; nếu hoá ra là AI đọc nhầm, bấm "Sửa
                                  mã" ở cột Ký hiệu/Số rồi lưu — "Trùng" tự gỡ. */}
                              {!daChi && it.matchStatus === 'duplicate' && (() => {
                                const khac = timDongTrungKhac(items, it);
                                return khac.length > 0 && (
                                  <div className='small text-muted'>
                                    Trùng với: {khac.map(nhanDong).join(', ')}
                                  </div>
                                );
                              })()}
                            </td>
                            <td>
                              <Badge bg={misa.bg} text={misa.bg === 'warning' ? 'dark' : undefined} title={misa.hint}>{misa.nhan}</Badge>
                            </td>
                            <td style={{ minWidth: 170 }}>
                              {(() => {
                                // Mã điền sẵn mà chưa ai xác nhận: viền vàng + nói rõ
                                // nguồn, để người duyệt biết phải kiểm (kế toán,
                                // 20/10/2026). Bấm Duyệt hoặc tự chọn là hết cảnh báo.
                                const doan = maDoan(it);
                                return doan && (
                                  <div
                                    className='small text-warning-emphasis mb-1'
                                    title='Mã này do hệ thống điền sẵn, chưa ai xác nhận. Kiểm tra đúng người rồi bấm Duyệt, hoặc chọn người khác.'
                                  >
                                    {doan === 'bien-so' ? 'Mã đoán theo biển số' : 'Mã điền sẵn, chưa rõ nguồn'}
                                  </div>
                                );
                              })()}
                              <div className={maDoan(it) ? 'border border-2 border-warning rounded' : undefined}>
                              <TeacherPicker
                                /* Đến đây ô này thường ĐÃ có sẵn giá trị: backend điền mã
                                 * suy từ biển số nếu suy được; không suy được thì để
                                 * TRỐNG (từ 13/10/2026 — không còn đoán mò HOANHST, để
                                 * người duyệt tự gõ đúng tên). Điền sẵn nhưng KHÔNG tự
                                 * duyệt — cột "Duyệt" vẫn phải do người bấm. */
                                value={it.drivingTeacher || ''}
                                teachers={teachers}
                                disabled={daChi}
                                title={daChi ? 'Hoá đơn đã chi — không đổi người nhận' : undefined}
                                // "Bỏ gán" (ma rỗng) thì bỏ luôn duyệt — một dòng "Đã
                                // duyệt" mà không có giáo viên là nhãn sai.
                                onChange={(ma) => suaDong(it._id, { drivingTeacher: ma, reviewed: !!ma })}
                              />
                              </div>
                              {canXacNhan && (
                                <Form.Check
                                  id={`xac-nhan-doi-${it._id}`}
                                  className='small text-warning-emphasis mt-1'
                                  checked={!!it.confirmTeacherChange}
                                  disabled={it.status === 'committed'}
                                  label={`Xác nhận đổi từ ${it.matchedTeacher}`}
                                  onChange={(e) => suaDong(it._id, { confirmTeacherChange: e.target.checked })}
                                />
                              )}
                            </td>
                            <td className='text-center'>
                              {/* Nút chứ không phải ô tick: duyệt là một HÀNH ĐỘNG để
                                  hoá đơn được ghi vào sổ chi tiền cho giáo viên, không
                                  phải một thuộc tính để gạt qua gạt lại. Nút còn nói rõ
                                  trạng thái bằng chữ và màu, thay vì bắt người dùng đọc
                                  một ô vuông nhỏ ở giữa bảng nhiều dòng.
                                  Giáo viên nay đã được điền sẵn, nên không có nút này
                                  thì người chấp nhận mặc định không có cách nào duyệt mà
                                  không phải đổi sang người khác rồi đổi lại. */}
                              <Button
                                size='sm'
                                variant={it.reviewed ? 'success' : 'outline-primary'}
                                disabled={it.status === 'committed' || daChi || dangDuyet.has(it._id)}
                                title={it.reviewed ? 'Bấm để bỏ duyệt' : 'Bấm để duyệt dòng này'}
                                onClick={() => doiDuyet(it)}
                              >
                                {dangDuyet.has(it._id)
                                  ? <Spinner size='sm' animation='border' />
                                  : (it.reviewed ? 'Đã duyệt' : 'Duyệt')}
                              </Button>
                            </td>
                            <td className='small'>
                              {it.status === 'failed' && <span className='text-danger'>{it.error || 'đọc lỗi'}</span>}
                              {it.mathOk === false && (
                                <span className='text-warning' title={(it.mathNotes || []).join('; ')}>
                                  lệch số học
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {items.some((i) => i.status === 'failed') && (
                  <Button
                    size='sm'
                    variant='outline-secondary'
                    className='mt-2'
                    onClick={async () => {
                      await invoiceOcrService.retryBatch(b._id);
                      await loadBatch(b._id);
                      toast.info('Đang đọc lại các dòng lỗi');
                    }}
                  >
                    Đọc lại các dòng lỗi
                  </Button>
                )}
              </Card.Body>
            </Card>
          )}
          {b && (
            <DoiChieuMisaModal
              show={moDoiChieu}
              doiChieu={b.doiChieu || null}
              items={items}
              dangGui={guiDoiChieu}
              onBatDau={batDauDoiChieu}
              onDong={() => setMoDoiChieu(false)}
            />
          )}
        </Col>
      </Row>

      <SuaMaModal
        item={dangSua}
        onHide={() => setDangSua(null)}
        onSave={async (corrections) => {
          await suaDong(dangSua._id, { corrections });
          setDangSua(null);
        }}
      />
    </Container>
  );
}

/**
 * Sửa ký hiệu/số/MST khi AI đọc sai — ba trường QUYẾT ĐỊNH một hoá đơn có bị
 * coi là trùng hay không (xem `invoice-ocr-identity.util` phía backend).
 *
 * Trang này trước đây không có cách sửa trường nào cả, nên một "Trùng" do
 * đọc nhầm không có lối thoát — người duyệt chỉ thấy chữ "Trùng" treo mãi,
 * không có cách tự sửa (yêu cầu kế toán, 13/10/2026).
 */
function SuaMaModal({ item, onHide, onSave }) {
  const [kyHieu, setKyHieu] = useState('');
  const [so, setSo] = useState('');
  const [mst, setMst] = useState('');

  useEffect(() => {
    if (!item) return;
    setKyHieu(valueOf(item, 'Series') || '');
    setSo(valueOf(item, 'InvoiceNo') || '');
    setMst(valueOf(item, 'SellerTaxCode') || '');
  }, [item]);

  if (!item) return null;

  const luu = () => {
    const corrections = {};
    if (kyHieu !== (valueOf(item, 'Series') || '')) corrections.Series = kyHieu;
    if (so !== (valueOf(item, 'InvoiceNo') || '')) corrections.InvoiceNo = so;
    if (mst !== (valueOf(item, 'SellerTaxCode') || '')) corrections.SellerTaxCode = mst;
    if (!Object.keys(corrections).length) return onHide();
    return onSave(corrections);
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Sửa định danh: {item.originalName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='light' className='py-2 small'>
          Chỉ sửa khi AI đọc sai — đây là ba trường quyết định hoá đơn có bị
          coi là trùng với một dòng khác trong cùng đợt hay không. Sửa xong,
          "Trùng" (ở dòng này lẫn dòng kia) sẽ tự tính lại.
        </Alert>
        <Form.Group className='mb-2'>
          <Form.Label className='small mb-0'>Ký hiệu</Form.Label>
          <Form.Control size='sm' value={kyHieu} onChange={(e) => setKyHieu(e.target.value)} />
        </Form.Group>
        <Form.Group className='mb-2'>
          <Form.Label className='small mb-0'>Số hoá đơn</Form.Label>
          <Form.Control size='sm' value={so} onChange={(e) => setSo(e.target.value)} />
        </Form.Group>
        <Form.Group className='mb-2'>
          <Form.Label className='small mb-0'>MST người bán</Form.Label>
          <Form.Control size='sm' value={mst} onChange={(e) => setMst(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>Huỷ</Button>
        <Button onClick={luu}>Lưu</Button>
      </Modal.Footer>
    </Modal>
  );
}
